import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Tv, Film, Radio, Settings, Monitor, Zap, Activity, Layers, Star, RefreshCw, Search } from 'lucide-react';
import * as api from '../../services/api';
import OnboardingWizard from './OnboardingWizard';

function CustomerDashboard() {
  const [profile, setProfile] = useState(null);
  const [providerStatus, setProviderStatus] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [epgMatching, setEpgMatching] = useState(false);
  const [epgResult, setEpgResult] = useState(null);
  const [epgProgress, setEpgProgress] = useState(null);
  const [failoverHealth, setFailoverHealth] = useState(null);
  const [settingPrimary, setSettingPrimary] = useState(null);
 // Phase 3 Item 16: unified content search
 const [searchQuery, setSearchQuery] = useState('');
 const [searchResults, setSearchResults] = useState(null);
 const [searchLoading, setSearchLoading] = useState(false);
 let _searchTimer = null;

  // Phase 3 Item 16: debounced search
 const handleSearch = (q) => {
 setSearchQuery(q);
 if (_searchTimer) clearTimeout(_searchTimer);
 if (!q || q.trim().length < 2) { setSearchResults(null); return; }
 setSearchLoading(true);
 _searchTimer = setTimeout(async () => {
 try {
 const res = await api.customerSearch(q.trim());
 setSearchResults(res.data || { channels: [], movies: [], series: [] });
 } catch (e) { setSearchResults({ channels: [], movies: [], series: [] }); }
 setSearchLoading(false);
 }, 300);
 };

 useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [profRes, statusRes, onboardRes, healthRes] = await Promise.all([
        api.customerGetProfile(),
        api.customerGetProviderStatus(),
        api.customerGetOnboardingStatus(),
        api.customerGetFailoverHealth().catch(() => ({ data: null }))
      ]);
      setProfile(profRes.data);
      setProviderStatus(statusRes.data);
      setOnboarding(onboardRes.data);
      setFailoverHealth(healthRes.data);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const pollEpgJob = async () => {
    try {
      const res = await api.customerEpgAutoMatchStatus();
      const job = res.data;
      setEpgProgress(job);
      if (job.status === 'fetching' || job.status === 'running') {
        setTimeout(pollEpgJob, 2000);
        return;
      }
      setEpgMatching(false);
      if (job.status === 'complete') {
        setEpgResult({ matched: job.matched, total: job.total });
        await loadAll();
      } else if (job.status === 'error') {
        setEpgResult({ error: job.error || 'EPG match failed' });
      }
    } catch (err) {
      setEpgMatching(false);
      setEpgResult({ error: 'EPG match failed' });
    }
  };

  const handleEpgAutoMatch = async () => {
    setEpgMatching(true);
    setEpgResult(null);
    setEpgProgress(null);
    try {
      const res = await api.customerEpgAutoMatch();
      setEpgProgress(res.data);
      if (res.data.status === 'fetching' || res.data.status === 'running') {
        setTimeout(pollEpgJob, 1500);
      } else {
        setEpgMatching(false);
        setEpgResult(res.data);
      }
    } catch (err) {
      setEpgMatching(false);
      setEpgResult({ error: 'EPG match failed' });
    }
  };


  const handleSetPrimary = async (lineId) => {
    if (!lineId || settingPrimary) return;
    setSettingPrimary(lineId);
    try {
      await api.customerSetPrimaryProviderLine(lineId);
      await loadAll();
    } catch (err) {
      console.error('Failed to set primary provider line:', err);
      alert(err.response?.data?.error || 'Failed to set primary provider');
    } finally {
      setSettingPrimary(null);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return 'Never';
    const diffMins = Math.floor((Date.now() - new Date(dateStr)) / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return diffMins + 'm ago';
    if (diffMins < 1440) return Math.floor(diffMins / 60) + 'h ago';
    return Math.floor(diffMins / 1440) + 'd ago';
  };

  if (loading) return <div className="page-header"><h2>Loading...</h2></div>;
  if (!profile) return <div className="page-header"><h2>Failed to load dashboard</h2></div>;

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const xtreamUser = profile.xtream?.xtream_username || 'Not set';
  const host = 'panel.streaminfinitytv.com';
  const epgCoverage = profile.stats?.channels ? Math.round((profile.stats.epg || 0) / profile.stats.channels * 100) : 0;

  return (
    <div>
      {onboarding?.show_wizard && (
        <OnboardingWizard
          status={onboarding}
          onComplete={loadAll}
          onApplyStarter={(packId) => api.customerApplyStarterPack(packId)}
        />
      )}

      <div className="page-header">
        <h2>Welcome, {user.username || 'Customer'}</h2>
        <p style={{ color: '#6b7280' }}>Your unified multi-provider IPTV service</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card"><div className="stat-icon"><Radio size={24} /></div><div className="stat-value">{profile.stats?.channels?.toLocaleString()}</div><div className="stat-label">Live Channels</div></div>
        <div className="stat-card"><div className="stat-icon"><Film size={24} /></div><div className="stat-value">{profile.stats?.movies?.toLocaleString()}</div><div className="stat-label">Movies</div></div>
        <div className="stat-card"><div className="stat-icon"><Tv size={24} /></div><div className="stat-value">{profile.stats?.series?.toLocaleString()}</div><div className="stat-label">TV Series</div></div>
        <div className="stat-card"><div className="stat-icon"><Monitor size={24} /></div><div className="stat-value">{profile.stats?.providers || 0}</div><div className="stat-label">Providers</div></div>
      </div>

      <div className="stats-grid" style={{ marginTop: '15px' }}>
        <div className="stat-card"><div className="stat-icon"><Zap size={24} /></div><div className="stat-value">{profile.stats?.epg?.toLocaleString() || 0}</div><div className="stat-label">Channels with EPG</div></div>
        <div className="stat-card"><div className="stat-icon"><Zap size={24} /></div><div className="stat-value">{epgCoverage}%</div><div className="stat-label">EPG Coverage</div></div>
        <div className="stat-card"><div className="stat-icon"><Activity size={24} /></div><div className="stat-value">{providerStatus?.connections?.active_ips || 0}</div><div className="stat-label">Active Connections</div></div>
      </div>

      <div className="table-container" style={{ marginTop: '25px' }}>
        <div className="table-header">
          <h3>Provider Health</h3>
          <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 400 }}>Primary is preferred for playback; backups are used automatically if it fails</span>
          {(() => {
            // Issue 118: suggest switching primary when a backup probes clearly healthier
            const lines = failoverHealth?.lines || [];
            const list = providerStatus?.providers || [];
            if (!list.length || !lines.length) return null;
            const primary = list.find(p => p.is_primary);
            if (!primary) return null;
            const primaryProbe = lines.find(l => Number(l.provider_line_id || l.line_id || l.id) === Number(primary.id) || l.line_name === primary.name || l.name === primary.name);
            const primaryRate = primaryProbe?.success_rate;
            let best = null;
            for (const p of list) {
              if (p.is_primary) continue;
              const probe = lines.find(l => Number(l.provider_line_id || l.line_id || l.id) === Number(p.id) || l.line_name === p.name || l.name === p.name);
              const rate = probe?.success_rate;
              if (rate == null || (probe?.samples_probed || 0) < 3) continue;
              if (primaryRate != null && rate < (primaryRate + 15)) continue;
              if (primaryRate == null && rate < 80) continue;
              if (!best || rate > best.rate) best = { p, rate };
            }
            if (!best) return null;
            return (
              <div style={{ margin: '12px 0', padding: '12px 14px', background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: '8px', fontSize: '13px' }}>
                <strong>Suggested primary:</strong> {best.p.name} looks healthier
                ({best.rate}% probe success{primaryRate != null ? ` vs ${primaryRate}% on current primary` : ''}).
                Switching primary is priority-only — no re-sync.
                <button
                  className="btn btn-secondary"
                  style={{ marginLeft: '10px' }}
                  disabled={!!settingPrimary}
                  onClick={() => handleSetPrimary(best.p.id)}
                >
                  {settingPrimary === best.p.id ? 'Saving…' : `Set ${best.p.name} as Primary`}
                </button>
              </div>
            );
          })()}

        </div>
        <table>
          <thead><tr><th>Provider</th><th>Role</th><th>Status</th><th>Last Sync</th><th>Channels</th><th>Movies</th><th>Series</th><th></th></tr></thead>
          <tbody>
            {(providerStatus?.providers || []).map(p => (
              <tr key={p.id}>
                <td><strong>{p.name}</strong><br /><span style={{ fontSize: '12px', color: '#9ca3af' }}>{p.type?.toUpperCase()}</span></td>
                <td>
                  {p.is_primary
                    ? <span className="badge badge-success">Primary</span>
                    : <span className="badge badge-info">Backup</span>}
                </td>
                <td><span className={`badge badge-${p.status === 'online' ? 'success' : p.status === 'offline' ? 'danger' : 'info'}`}>{p.status || 'online'}</span></td>
                <td>{formatTime(p.last_synced)}</td>
                <td>{p.channels?.enabled}/{p.channels?.total}</td>
                <td>{p.movies?.enabled}/{p.movies?.total}</td>
                <td>{p.series?.enabled}/{p.series?.total}</td>
                <td>
                  {!p.is_primary && (
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      disabled={!!settingPrimary}
                      onClick={() => handleSetPrimary(p.id)}
                      title="Prefer this provider first for playback"
                    >
                      {settingPrimary === p.id ? 'Saving…' : 'Set as Primary'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {(!providerStatus?.providers || providerStatus.providers.length === 0) && (
              <tr><td colSpan={8} style={{ textAlign: 'center', color: '#9ca3af' }}>No provider lines assigned yet</td></tr>
            )}
          </tbody>
        </table>
      </div>


      {failoverHealth?.summary && (
        <div className="table-container" style={{ marginTop: '25px' }}>
          <div className="table-header">
            <h3>Failover Health</h3>
            <span className={`badge badge-${failoverHealth.summary.failover_ready ? 'success' : 'warning'}`} style={{ marginLeft: '10px' }}>
              {failoverHealth.summary.failover_ready ? 'Backup ready' : 'Needs attention'}
            </span>
          </div>
          <div style={{ padding: '16px 20px', display: 'flex', gap: '24px', flexWrap: 'wrap', borderBottom: '1px solid #e5e7eb' }}>
            <div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: (failoverHealth.summary.overall_success_rate ?? 0) >= 80 ? '#10b981' : '#f59e0b' }}>
                {failoverHealth.summary.overall_success_rate != null ? `${failoverHealth.summary.overall_success_rate}%` : '—'}
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>Stream probe success</div>
            </div>
            <div>
              <div style={{ fontSize: '24px', fontWeight: '700' }}>{failoverHealth.summary.failovers_7d || 0}</div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>Failovers (7 days)</div>
            </div>
            <div>
              <div style={{ fontSize: '14px', fontWeight: '600' }}>{formatTime(failoverHealth.summary.last_probe_at)}</div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>Last automated probe</div>
            </div>
          </div>
          {(failoverHealth.lines || []).length > 0 && (
            <table>
              <thead><tr><th>Provider Line</th><th>Probe Success</th><th>Samples</th><th>Avg Latency</th><th>Last Error</th></tr></thead>
              <tbody>
                {failoverHealth.lines.map((line) => (
                  <tr key={line.provider_line_id}>
                    <td><strong>{line.provider_name}</strong></td>
                    <td>
                      <span className={`badge badge-${(line.success_rate ?? 0) >= 80 ? 'success' : (line.success_rate ?? 0) >= 50 ? 'warning' : 'danger'}`}>
                        {line.success_rate != null ? `${line.success_rate}%` : '—'}
                      </span>
                    </td>
                    <td>{line.ok_count}/{line.samples_probed}</td>
                    <td>{line.avg_latency_ms != null ? `${line.avg_latency_ms}ms` : '—'}</td>
                    <td style={{ fontSize: '12px', color: '#ef4444', maxWidth: '200px' }}>{line.last_error || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {(failoverHealth.recent_failures || []).length > 0 && (
            <div style={{ padding: '12px 20px 20px' }}>
              <h4 style={{ margin: '0 0 8px', fontSize: '14px' }}>Recent probe failures</h4>
              <table>
                <thead><tr><th>Channel</th><th>Provider</th><th>Status</th><th>When</th></tr></thead>
                <tbody>
                  {failoverHealth.recent_failures.slice(0, 5).map((f, i) => (
                    <tr key={i}>
                      <td>{f.content_name}</td>
                      <td>{f.provider_name}</td>
                      <td><span className="badge badge-danger">{f.probe_status}</span></td>
                      <td>{formatTime(f.probed_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p style={{ padding: '0 20px 16px', margin: 0, fontSize: '12px', color: '#9ca3af' }}>
            Automated probes sample live channels on each provider line every 30 minutes. When one line fails, playback should switch to your backup line automatically.
          </p>
        </div>
      )}


      {(providerStatus?.failovers || []).length > 0 && (
        <div className="table-container" style={{ marginTop: '20px' }}>
          <div className="table-header"><h3>Recent Backup Failovers</h3></div>
          <table>
            <thead><tr><th>Content</th><th>Type</th><th>From</th><th>To</th><th>When</th></tr></thead>
            <tbody>
              {providerStatus.failovers.map((f, i) => (
                <tr key={i}>
                  <td>{f.content_name}</td>
                  <td>{f.content_type}</td>
                  <td>{f.from_provider}</td>
                  <td>{f.to_provider}</td>
                  <td>{formatTime(f.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: '15px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
        <button onClick={handleEpgAutoMatch} disabled={epgMatching} className="btn btn-secondary" style={{ padding: '8px 16px' }}>
          <RefreshCw size={16} style={{ marginRight: '6px', animation: epgMatching ? 'spin 1s linear infinite' : 'none' }} />
          {epgMatching ? (epgProgress?.phase === 'fetching' ? 'Fetching EPG...' : 'Matching EPG...') : 'Refresh EPG'}
        </button>
        {epgMatching && epgProgress && (
          <span style={{ color: '#6b7280', fontSize: '14px' }}>
            {epgProgress.phase === 'fetching'
              ? 'Downloading provider EPG data...'
              : `${epgProgress.processed?.toLocaleString() || 0} / ${epgProgress.total?.toLocaleString() || '?'} channels · ${epgProgress.matched?.toLocaleString() || 0} matched`}
          </span>
        )}
        {!epgMatching && epgResult && !epgResult.error && (
          <span style={{ color: '#10b981', fontSize: '14px' }}>Matched {epgResult.matched} of {epgResult.total} channels</span>
        )}
        {epgResult?.error && <span style={{ color: '#ef4444', fontSize: '14px' }}>{epgResult.error}</span>}
      </div>

      <div className="table-container" style={{ marginTop: '25px' }}>
        <div className="table-header"><h3>Connection Details</h3></div>
        <div style={{ padding: '20px' }}>
          <table><tbody>
            <tr><td style={{ fontWeight: '600', width: '140px' }}>Server URL</td><td><code>https://{host}</code></td></tr>
            <tr><td style={{ fontWeight: '600' }}>Xtream Username</td><td><code>{xtreamUser}</code></td></tr>
          </tbody></table>
        </div>
      </div>

      <div className="table-container" style={{ marginTop: '20px' }}>
        <div className="table-header"><h3>Support</h3></div>
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <button className="btn btn-secondary" onClick={async () => {
            setLoading(true);
            try {
              const res = await api.customerSubmitDiagnostic();
              alert(res.data.message);
            } catch (e) {
              alert('Failed: ' + (e.response?.data?.error || e.message));
            } finally { setLoading(false); }
          }}>Report Issue / Run Diagnostic</button>
          <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px' }}>Collects system info and emails support with a reference code.</p>
        </div>
      </div>

      {/* Phase 3 Item 16: Unified content search */}
      <div style={{ marginBottom: '20px', marginTop: '20px' }}>
        <div style={{ position: 'relative', maxWidth: '500px' }}>
          <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input type="text" placeholder="Search channels, movies, series..." value={searchQuery} onChange={e => handleSearch(e.target.value)} style={{ width: '100%', padding: '10px 12px 10px 36px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} />
        </div>
        {searchResults && (
          <div style={{ marginTop: '8px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px', maxHeight: '300px', overflow: 'auto' }}>
            {searchLoading ? <p style={{ color: '#9ca3af', fontSize: '13px' }}>Searching...</p> : (<>
              {searchResults.channels?.length > 0 && <div style={{ marginBottom: '8px' }}><strong style={{ fontSize: '12px', color: '#6b7280' }}>Channels ({searchResults.channels.length})</strong>{searchResults.channels.slice(0,5).map(ch => <div key={ch.id} style={{ fontSize: '13px', padding: '4px 0' }}><Link to="/channels">{ch.name}</Link></div>)}</div>}
              {searchResults.movies?.length > 0 && <div style={{ marginBottom: '8px' }}><strong style={{ fontSize: '12px', color: '#6b7280' }}>Movies ({searchResults.movies.length})</strong>{searchResults.movies.slice(0,5).map(m => <div key={m.id} style={{ fontSize: '13px', padding: '4px 0' }}><Link to="/movies">{m.name}</Link></div>)}</div>}
              {searchResults.series?.length > 0 && <div><strong style={{ fontSize: '12px', color: '#6b7280' }}>Series ({searchResults.series.length})</strong>{searchResults.series.slice(0,5).map(s => <div key={s.id} style={{ fontSize: '13px', padding: '4px 0' }}><Link to="/series">{s.name}</Link></div>)}</div>}
              {(!searchResults.channels?.length && !searchResults.movies?.length && !searchResults.series?.length) && <p style={{ color: '#9ca3af', fontSize: '13px' }}>No results found.</p>}
            </>)}
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px', marginTop: '20px' }}>
        <Link to="/channels" className="btn btn-primary" style={{ textAlign: 'center', padding: '15px' }}><Radio size={18} /> Channels</Link>
        <Link to="/movies" className="btn btn-secondary" style={{ textAlign: 'center', padding: '15px' }}><Film size={18} /> Movies</Link>
        <Link to="/series" className="btn btn-secondary" style={{ textAlign: 'center', padding: '15px' }}><Tv size={18} /> Series</Link>
        <Link to="/duplicates" className="btn btn-secondary" style={{ textAlign: 'center', padding: '15px' }}><Layers size={18} /> Duplicates</Link>
        <Link to="/settings" className="btn btn-secondary" style={{ textAlign: 'center', padding: '15px' }}><Settings size={18} /> Settings</Link>
      </div>

      <p style={{ marginTop: '16px', fontSize: '13px', color: '#9ca3af' }}>
        <Star size={14} style={{ verticalAlign: 'middle' }} /> Tip: Star your favorites on Channels, Movies, or Series — they appear as <strong>⭐ My List</strong> in your IPTV app.
      </p>
    </div>
  );
}

export default CustomerDashboard;
